import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

/**
 * Custom hook reusing MediVoyage's real-time transcript recording pipeline.
 * Captures actual microphone & avatar speaker audio during AI Counsellor and AI Interviewer sessions,
 * transcribes REAL dialogue spoken during the session without generating synthetic text,
 * streams transcript lines to backend DB separately, and tracks real-time transcript lines for latency testing.
 */
export function useTranscriptRecorder({ isSessionActive, currentSessionId, mode = 'counsellor', aiSpeakerLabel = 'AI Counsellor', onSpeechDetected, onSessionExpired, beyAgentId = '' }) {
  const [liveTranscripts, setLiveTranscripts] = useState([]);
  const [activeSpeaker, setActiveSpeaker] = useState('Student');
  // The server enforces the interview deadline; these track whether we have
  // already told the UI (so a 409 storm only produces one notification).
  const onSessionExpiredRef = useRef(onSessionExpired);
  const sessionExpiredRef = useRef(false);
  onSessionExpiredRef.current = onSessionExpired;

  const activeSpeakerRef = useRef('Student');
  const isListeningRef = useRef(false);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const onSpeechDetectedRef = useRef(onSpeechDetected);

  useEffect(() => {
    onSpeechDetectedRef.current = onSpeechDetected;
  }, [onSpeechDetected]);

  // Reset transcripts state whenever a new session starts
  useEffect(() => {
    if (isSessionActive && currentSessionId) {
      setLiveTranscripts([]);
      setActiveSpeaker('Student');
    }
  }, [isSessionActive, currentSessionId]);

  // Turn management: after student speaks and pauses for 1.8s, turn switches to AI Avatar speaker
  const handleSpeechTurnPause = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      const nextTurn = activeSpeakerRef.current === 'Student' ? aiSpeakerLabel : 'Student';
      activeSpeakerRef.current = nextTurn;
      setActiveSpeaker(nextTurn);
    }, 1800);
  }, [aiSpeakerLabel]);

  // Save single text transcript line to backend DB internally
  const recordTranscriptLine = useCallback(async (customSpeaker, text) => {
    if (!currentSessionId || !text || !text.trim()) return;
    const lineSpeaker = customSpeaker || activeSpeakerRef.current || 'Student';
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const newLine = {
      speaker: lineSpeaker,
      message: text.trim(),
      timestamp: timeStr
    };

    if (onSpeechDetectedRef.current) {
      try { onSpeechDetectedRef.current(text.trim(), lineSpeaker); } catch (_) {}
    }

    setLiveTranscripts(prev => {
      if (prev.length > 0 && prev[prev.length - 1].message === newLine.message && prev[prev.length - 1].speaker === lineSpeaker) {
        return prev;
      }
      return [...prev, newLine];
    });

    handleSpeechTurnPause();

    try {
      await api.post(`/agent-engine/${mode}/${currentSessionId}/heartbeat`, {
        transcript: `[${lineSpeaker}] ${text.trim()}`
      });
    } catch (err) {
      // 409 = the server-side interview deadline has passed. Stop trying to
      // record and let the UI end the session; retrying would drop lines.
      if (err?.response?.status === 409) {
        if (!sessionExpiredRef.current) {
          sessionExpiredRef.current = true;
          try {
            onSessionExpiredRef.current?.(err.response?.data?.detail);
          } catch (_) {}
        }
        return;
      }
      console.warn('[TranscriptRecorder] Failed to save transcript line:', err);
    }

  }, [currentSessionId, mode, handleSpeechTurnPause]);

  // Start browser Web Speech API for the Student's voice
  useEffect(() => {
    if (!isSessionActive || !currentSessionId) {
      return;
    }

    let isCancelled = false;
    isListeningRef.current = true;

    // 1. Web Speech API for actual spoken speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        let speechDebounceTimer = null;
        let lastProcessedText = '';

        recognition.onresult = (event) => {
          if (!isListeningRef.current || isCancelled) return;
          
          let fullTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            fullTranscript += event.results[i][0].transcript;
          }

          if (!fullTranscript || !fullTranscript.trim()) return;
          const cleanText = fullTranscript.trim();

          const isFinalEvent = Boolean(event.results[event.resultIndex]?.isFinal);

          // Only signal speech detected if it is final speech or substantial real dialogue
          if (isFinalEvent && cleanText.length >= 4) {
            if (onSpeechDetectedRef.current) {
              try { onSpeechDetectedRef.current(cleanText, 'Student'); } catch (_) {}
            }
          } else if (cleanText.length >= 8 && cleanText.includes(' ')) {
            if (onSpeechDetectedRef.current) {
              try { onSpeechDetectedRef.current(cleanText, 'Student'); } catch (_) {}
            }
          }

          if (cleanText === lastProcessedText) return;

          if (isFinalEvent) {
            if (speechDebounceTimer) clearTimeout(speechDebounceTimer);
            lastProcessedText = cleanText;
            recordTranscriptLine('Student', cleanText);
            return;
          }

          if (speechDebounceTimer) clearTimeout(speechDebounceTimer);
          speechDebounceTimer = setTimeout(() => {
            if (cleanText && cleanText !== lastProcessedText && cleanText.length > 3) {
              lastProcessedText = cleanText;
              recordTranscriptLine('Student', cleanText);
            }
          }, 1200);
        };

        recognition.onerror = (err) => {
          console.warn('[TranscriptRecorder] WebSpeech error:', err.error);
        };

        recognition.onend = () => {
          if (isListeningRef.current) {
            try { recognition.start(); } catch (e) {}
          }
        };

        recognition.start();
        recognitionRef.current = recognition;
      } catch (err) {
        console.warn('[TranscriptRecorder] WebSpeech init notice:', err);
      }
    }

    return () => {
      isCancelled = true;
      isListeningRef.current = false;

      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);

      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
        recognitionRef.current = null;
      }
    };
  }, [isSessionActive, currentSessionId, recordTranscriptLine]);

  // Finalize session function
  const finalizeSession = useCallback(async (finalCallId, extraPayload = {}) => {
    if (!currentSessionId) return null;
    try {
      const payload = { ...extraPayload };
      if (finalCallId) payload.callId = finalCallId;
      if (beyAgentId) payload.beyAgentId = beyAgentId; // lets backend clean up the per-session agent
      
      const res = await api.post(`/agent-engine/${mode}/${currentSessionId}/complete`, payload);
      const resData = res.data || {};
      let report = resData?.final_index || resData?.report || {};
      if (typeof report === 'string') {
        try { report = JSON.parse(report); } catch (e) { report = {}; }
      }
      if (resData?.transcript) {
        report.transcript = resData.transcript;
      }
      return {
        ...resData,
        report: report
      };
    } catch (err) {
      console.error('[TranscriptRecorder] Finalize session error:', err);
      return null;
    }
  }, [currentSessionId, mode, beyAgentId]);

  return { finalizeSession, liveTranscripts, recordTranscriptLine, activeSpeaker, setActiveSpeaker };
}
